import React, { useMemo, useState } from 'react'
import { Download, FileSearch, Hash } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'

import { pageCountFor, useClampPage } from '@/features/cases/paging'
import { dateTime, localDate, num, relativeTime } from '@/lib/format'

import { useAuditCount, useAuditEvents, useAuditPrefixes, type AuditEvent } from './auditApi'
import { AuditPager, type AuditPageSize } from './AuditPager'

/**
 * Regulator-facing audit log viewer at /admin/audit. AUDIT_VIEW-gated.
 *
 * Quick-filter chips at the top encode the common prosecutorial queries:
 *   - Conduct overrides: every CONDUCT.*_OVERRIDE_USED in the window.
 *   - User changes: USER.* events (created, suspended, offboarded, role grants).
 *   - PTP state changes: PTP.OPENED / PTP.BROKEN / PTP.KEPT / retroactive.
 *   - Logins: SECURITY.LOGIN_SUCCEEDED / FAILED.
 *
 * Below the chips: date range, actor empId, event-type prefix, source
 * dropdown. Results render newest-first, a page at a time with the total;
 * clicking a row expands the payload. "Download CSV" saves the page on screen.
 */
export function AuditPage() {
  // Date range — default to the last 7 days, ISO-encoded for the API.
  const today = useMemo(() => isoDate(new Date()), [])
  const sevenDaysAgo = useMemo(
    () => isoDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
    [],
  )
  const [fromDate, setFromDate] = useState(sevenDaysAgo)
  const [toDate, setToDate] = useState(today)
  const [actor, setActor] = useState('')
  const [typePrefix, setTypePrefix] = useState('')
  const [source, setSource] = useState<'ALL' | 'USER' | 'SYSTEM' | 'RECON' | 'INGEST_BATCH'>('ALL')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // Numbered pages over the server's offset. Back to the first page
  // whenever a filter or the page size changes.
  const [pageSize, setPageSize] = useState<AuditPageSize>(50)
  const [page, setPage] = useState(0)
  // The log keeps growing while someone pages through it (reading it adds
  // rows). Pin the window's end to when the filters were set, so the total
  // and every page count the same events.
  const [asOf, setAsOf] = useState(() => new Date().toISOString())

  // Backend expects ISO instants; clamp the dates to start/end of day.
  const fromIso = `${fromDate}T00:00:00Z`
  const toIso = earlier(`${toDate}T23:59:59Z`, asOf)

  const query = {
    from: fromIso,
    to: toIso,
    ...(actor ? { actor: actor.trim().toUpperCase() } : {}),
    ...(typePrefix ? { type: typePrefix } : {}),
    ...(source !== 'ALL' ? { source } : {}),
  }

  const { data: events, isPending, isError, isPlaceholderData } = useAuditEvents({
    ...query, limit: pageSize, offset: page * pageSize,
  })
  const { data: total } = useAuditCount(query)
  const { data: prefixes } = useAuditPrefixes(fromIso, toIso)

  // A filter or the page size changed: back to the first page, and a fresh
  // window end. Adjusted during render, not in an effect, so the first load
  // does not fetch twice.
  const filterKey = [fromDate, toDate, actor, typePrefix, source, pageSize].join('|')
  const [shownFor, setShownFor] = useState(filterKey)
  if (shownFor !== filterKey) {
    setShownFor(filterKey)
    setPage(0)
    setAsOf(new Date().toISOString())
  }
  useClampPage(page, setPage, pageCountFor(total ?? null, pageSize))

  const counted = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of prefixes ?? []) map.set(p.prefix, p.count)
    return map
  }, [prefixes])

  function applyQuickFilter(p: string) {
    setTypePrefix(p)
    setExpandedId(null)
  }

  // All quick-filter chips a manager might want, in display order. We hide
  // every chip whose count is zero (except "All events") so the panel
  // doesn't visually noise out empty categories — drives the eye to the
  // groups that have actual activity in the window.
  const quickChips: Array<{ label: string; prefix: string; count: number }> = [
    { label: 'Conduct overrides', prefix: 'CONDUCT.',   count: counted.get('CONDUCT') ?? 0 },
    { label: 'User changes',      prefix: 'USER.',      count: counted.get('USER') ?? 0 },
    { label: 'PTP state',         prefix: 'PTP.',       count: counted.get('PTP') ?? 0 },
    { label: 'Case lifecycle',    prefix: 'CASE.',      count: counted.get('CASE') ?? 0 },
    { label: 'Allocations',       prefix: 'ALLOCATION.', count: counted.get('ALLOCATION') ?? 0 },
    { label: 'Reconciliation',    prefix: 'RECON.',     count: counted.get('RECON') ?? 0 },
    { label: 'Auth / logins',     prefix: 'SECURITY.',  count: counted.get('SECURITY') ?? 0 },
    { label: 'Tenant config',     prefix: 'TENANT.',    count: counted.get('TENANT') ?? 0 },
    { label: 'Cash receipts',     prefix: 'CASH.',      count: counted.get('CASH') ?? 0 },
    { label: 'Recoveries',        prefix: 'RECOVERY.',  count: counted.get('RECOVERY') ?? 0 },
  ]
  const totalCount = quickChips.reduce((a, c) => a + c.count, 0)
  const visibleChips = quickChips.filter(
    (c) => c.count > 0 || c.prefix === typePrefix,   // keep the active chip even if it's 0
  )

  function exportCsv() {
    if (!events) return
    const headers = [
      'recordedAt', 'occurredAt', 'eventType', 'source',
      'actorEmpId', 'actorType', 'aggregateType', 'aggregateId',
      'seq', 'payloadJson',
    ]
    const rows = events.map((e) => [
      e.recordedAt, e.occurredAt, e.eventType, e.source,
      e.actorEmpId ?? '', e.actorType, e.aggregateType, e.aggregateId,
      e.seq.toString(),
      JSON.stringify(e.payloadJson),
    ])
    const csv = [headers, ...rows]
      .map((r) => r.map(csvCell).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-${fromDate}_to_${toDate}${typePrefix ? '_' + typePrefix.replace(/[^A-Z]/g, '_') : ''}_page${page + 1}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
          Tenant administration
        </p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
          <FileSearch className="h-5 w-5" /> Audit log
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every state change in this tenant is here, hash-chained and
          immutable. Use the quick filters for common regulator queries, or
          drill in with the manual filters below.
        </p>
      </div>

      {/* Quick filters — the prosecutorial preset chips */}
      <Card>
        <CardHeader>
          <CardTitle>Quick filters</CardTitle>
          <CardDescription>Common regulator queries, one click.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <QuickChip
              active={typePrefix === ''}
              label="All events"
              count={totalCount}
              onClick={() => applyQuickFilter('')}
            />
            {visibleChips.map((c) => (
              <QuickChip
                key={c.prefix}
                active={typePrefix === c.prefix}
                label={c.label}
                count={c.count}
                onClick={() => applyQuickFilter(c.prefix)}
              />
            ))}
            {visibleChips.length === 0 && totalCount > 0 && (
              <span className="text-2xs uppercase tracking-wider text-muted-foreground">
                Pick a date range with activity, or clear filters
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Refine by date, actor, or specific event-type prefix.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <Label>From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} max={today} min={fromDate} />
            </div>
            <div>
              <Label>Actor (Emp ID)</Label>
              <Input
                value={actor}
                onChange={(e) => setActor(e.target.value.toUpperCase())}
                placeholder="TELE001"
              />
            </div>
            <div>
              <Label>Event type prefix</Label>
              <Input
                value={typePrefix}
                onChange={(e) => setTypePrefix(e.target.value.toUpperCase())}
                placeholder="CONDUCT.DNC_OVERRIDE_USED"
              />
              <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                LIKE prefix — partial OK
              </p>
            </div>
            <div>
              <Label>Source</Label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as typeof source)}
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-navy-900 hover:border-navy-200 focus-visible:outline-none focus-visible:border-navy-500"
              >
                <option value="ALL">Any source</option>
                <option value="USER">User</option>
                <option value="SYSTEM">System</option>
                <option value="RECON">Reconciliation</option>
                <option value="INGEST_BATCH">Ingest batch</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Events</CardTitle>
              <CardDescription>
                {total != null ? `${num(total)} match · newest first` : '—'}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!events || events.length === 0}
              title="Downloads the events on this page"
            >
              <Download className="h-3.5 w-3.5" />
              Download page (CSV)
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {isPending && <SkeletonRows rows={6} cols={5} />}
          {isError && (
            <div className="px-5 pb-4 text-sm text-negative">
              Couldn't load events — AUDIT_VIEW is required.
            </div>
          )}
          {events && events.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">No events match</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Widen the date range, clear the prefix, or pick a different actor.
              </p>
            </div>
          )}
          {events && events.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-5 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Event</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="px-3 py-2 font-medium">Actor</th>
                    <th className="px-3 py-2 font-medium">Aggregate</th>
                    <th className="px-5 py-2 font-medium">Seq</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <EventRow
                      key={e.eventId}
                      event={e}
                      expanded={expandedId === e.eventId}
                      onToggle={() =>
                        setExpandedId((cur) => (cur === e.eventId ? null : e.eventId))
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {events && events.length > 0 && (
            <AuditPager
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              rowsOnPage={events.length}
              total={total ?? null}
              emptyLabel="No matching events"
              loading={isPlaceholderData}
            />
          )}
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            Hash-chained · revoke-only · per-tenant
          </span>
        </CardFooter>
      </Card>
    </div>
  )
}

function EventRow({
  event,
  expanded,
  onToggle,
}: {
  event: AuditEvent
  expanded: boolean
  onToggle: () => void
}) {
  const tone = toneFor(event.eventType)
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-b border-slate-200 last:border-0 transition-colors hover:bg-navy-50"
      >
        <td className="px-5 py-2 text-muted-foreground" title={dateTime(event.recordedAt)}>
          {relativeTime(event.recordedAt)}
        </td>
        <td className="px-3 py-2">
          <Badge tone={tone}>{event.eventType}</Badge>
        </td>
        <td className="px-3 py-2 text-2xs uppercase tracking-wider text-muted-foreground">
          {event.source}
        </td>
        <td className="px-3 py-2 font-mono text-xs text-navy-900">
          {event.actorEmpId ?? <span className="text-muted-foreground">{event.actorType}</span>}
        </td>
        <td className="px-3 py-2 text-2xs uppercase tracking-wider text-navy-700">
          {event.aggregateType}
        </td>
        <td className="px-5 py-2 font-mono text-2xs tabular-nums text-muted-foreground">
          {event.seq}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-slate-200 bg-navy-50/30">
          <td colSpan={6} className="px-5 py-3">
            <div className="grid gap-3 sm:grid-cols-[10rem_1fr]">
              <span className="text-2xs uppercase tracking-wider text-muted-foreground">Aggregate id</span>
              <span className="break-all font-mono text-xs text-navy-900">
                <Hash className="mr-1 inline h-3 w-3" /> {event.aggregateId}
              </span>
              <span className="text-2xs uppercase tracking-wider text-muted-foreground">Event id</span>
              <span className="break-all font-mono text-2xs text-muted-foreground">{event.eventId}</span>
              <span className="text-2xs uppercase tracking-wider text-muted-foreground">Recorded at</span>
              <span className="font-mono text-xs">{dateTime(event.recordedAt)}</span>
              {event.occurredAt !== event.recordedAt && (
                <>
                  <span className="text-2xs uppercase tracking-wider text-muted-foreground">Occurred at</span>
                  <span className="font-mono text-xs">{dateTime(event.occurredAt)}</span>
                </>
              )}
              <span className="text-2xs uppercase tracking-wider text-muted-foreground">Payload</span>
              <pre className="overflow-x-auto rounded-md border border-slate-200 bg-white p-3 font-mono text-xs leading-relaxed text-navy-900">
                <HighlightedJson source={event.payloadJson} />
              </pre>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function QuickChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <Chip active={active} onClick={onClick} count={count}>
      {label}
    </Chip>
  )
}

function toneFor(eventType: string): 'navy' | 'positive' | 'warning' | 'negative' | 'slate' | 'info' {
  if (eventType.startsWith('CONDUCT.') && eventType.includes('OVERRIDE')) return 'warning'
  if (eventType === 'SECURITY.LOGIN_FAILED' || eventType === 'PTP.BROKEN' || eventType === 'USER.OFFBOARDED' || eventType === 'USER.SUSPENDED') return 'negative'
  if (eventType === 'PTP.KEPT' || eventType === 'PTP.RESOLVED_RETROACTIVELY' || eventType === 'USER.ACTIVATED' || eventType === 'USER.REACTIVATED' || eventType === 'CASE.CURED') return 'positive'
  if (eventType.startsWith('SECURITY.')) return 'info'
  return 'navy'
}

/** The earlier of two ISO instants. */
function earlier(a: string, b: string): string {
  return new Date(a) <= new Date(b) ? a : b
}

function isoDate(d: Date): string {
  return localDate(d)
}

function csvCell(v: string): string {
  if (/[",\n]/.test(v)) {
    return '"' + v.replace(/"/g, '""') + '"'
  }
  return v
}

/**
 * Lightweight JSON syntax highlighter — keys in navy-700, strings in
 * positive (green), numbers in info (blue), booleans/null in muted.
 * Avoids pulling in a syntax-highlighter dep for a single use case.
 *
 * <p>Input is already the pretty-printed string from prettyJson(). We
 * tokenize with a single regex pass and emit colored spans. If the
 * input isn't valid JSON we fall back to plain text (no false
 * highlighting on partially-malformed payloads).</p>
 */
function HighlightedJson({ source }: { source: string }) {
  const pretty = useMemo(() => prettyJson(source), [source])
  const looksJson = pretty.trim().startsWith('{') || pretty.trim().startsWith('[')
  if (!looksJson) {
    return <span className="text-muted-foreground">{pretty}</span>
  }
  // Token regex: strings (with optional trailing colon = key), numbers,
  // bools/null. Order matters — strings first so they swallow internal
  // structural punctuation.
  const tokenRe =
    /("(?:\\.|[^"\\])*")(\s*:)?|(\b-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|\b(true|false|null)\b/g
  const out: React.ReactNode[] = []
  let last = 0
  let i = 0
  for (const m of pretty.matchAll(tokenRe)) {
    const start = m.index ?? 0
    if (start > last) out.push(pretty.slice(last, start))
    const [, str, colon, num, kw] = m
    if (str !== undefined) {
      if (colon) {
        // key
        out.push(<span key={i++} className="text-navy-700">{str}</span>)
        out.push(colon)
      } else {
        out.push(<span key={i++} className="text-positive">{str}</span>)
      }
    } else if (num !== undefined) {
      out.push(<span key={i++} className="text-info">{num}</span>)
    } else if (kw !== undefined) {
      out.push(<span key={i++} className="text-muted-foreground">{kw}</span>)
    }
    last = start + m[0].length
  }
  if (last < pretty.length) out.push(pretty.slice(last))
  return <>{out}</>
}

function prettyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2)
  } catch {
    return s
  }
}
